import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { api } from '@api'
import { secureTokenStore } from '@store'
import { buildDiscoveryParams, type DiscoveryFilters } from '@utils'
import type {
  CheckInFilter,
  DiscoverEventsResponse,
  SimulatedPaymentResponse,
  EventAttendeesResponse,
  EventDetailResponse,
  EventEmailCampaign,
  EventInvitation,
  EventsConfig,
  MyEventsResponse,
  MyTicketResponse,
  OrganizerAttendee,
  OrganizerAttendeesResponse,
  OrganizerEvent,
  OrganizerStatsResponse,
  PublicEventResponse,
} from './types'

/** Root key shared by every event query, so one invalidation refreshes them all. */
export const EVENTS_QUERY_KEY = 'events'

/**
 * Its OWN root key, deliberately outside `EVENTS_QUERY_KEY`: this is the one
 * event query allowed onto disk on native (see `PERSISTED_QUERY_ROOTS` in
 * `App.tsx`), so "Mon billet" still reads once opened, offline, at the door.
 * Keeping it separate means nothing else under `events` (attendee lists,
 * sales figures…) gets persisted along with it.
 */
export const MY_TICKET_QUERY_KEY = 'my-ticket'

const isLoggedIn = () => !!secureTokenStore.getAccessToken()

/** Logged-out page reached through the QR code / invitation link. */
export const usePublicEvent = (code: string, options: { enabled?: boolean } = {}) =>
  useQuery<PublicEventResponse>({
    queryKey: [EVENTS_QUERY_KEY, 'public', code],
    queryFn: async () => (await api.get<PublicEventResponse>(`/public/events/${code}`)).data,
    enabled: !!code && (options.enabled ?? true),
    retry: false,
  })

export const useEventsConfig = () =>
  useQuery<EventsConfig>({
    queryKey: [EVENTS_QUERY_KEY, 'config'],
    queryFn: async () => (await api.get<EventsConfig>('/public/events-config')).data,
    staleTime: 5 * 60 * 1000,
  })

/** Same page for a logged-in user, with their role on the event. */
export const useEventByCode = (code: string, options: { enabled?: boolean } = {}) =>
  useQuery<EventDetailResponse>({
    queryKey: [EVENTS_QUERY_KEY, 'by-code', code],
    queryFn: async () => (await api.get<EventDetailResponse>(`/events/by-code/${code}`)).data,
    enabled: !!code && isLoggedIn() && (options.enabled ?? true),
    retry: false,
  })

export const useEventById = (eventId?: string) =>
  useQuery<EventDetailResponse>({
    queryKey: [EVENTS_QUERY_KEY, 'by-id', eventId],
    queryFn: async () => (await api.get<EventDetailResponse>(`/events/${eventId}`)).data,
    enabled: !!eventId && isLoggedIn(),
    // A 4xx (unknown / forbidden event) will not get better by asking again.
    retry: (failures, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status
      return !(status && status >= 400 && status < 500) && failures < 2
    },
  })

/**
 * "Mon billet" full screen: the same `GET /events/:id` the event page already
 * calls, reduced to just what the screen shows before it is cached — so what
 * survives to disk (and so what's readable offline once opened) is the
 * ticket, never the sales figures, other attendees, or organizer info that
 * `EventDetailResponse` also carries.
 */
export const useMyTicket = (eventId: string | undefined) =>
  useQuery<MyTicketResponse>({
    queryKey: [MY_TICKET_QUERY_KEY, eventId],
    queryFn: async () => {
      const [{ data: detail }, { data: profile }] = await Promise.all([
        api.get<EventDetailResponse>(`/events/${eventId}`),
        api.get<{ profile: { firstName: string; lastName: string } }>('/profile/me'),
      ])
      return {
        ticketCode: detail.viewer.ticketCode,
        firstName: profile.profile.firstName,
        lastName: profile.profile.lastName,
        event: {
          name: detail.event.name,
          startsAt: detail.event.startsAt,
          location: detail.event.location,
          phase: detail.event.phase,
        },
      }
    },
    enabled: !!eventId && isLoggedIn(),
  })

export const useEventAttendees = (
  eventId: string | undefined,
  search: string,
  enabled = true,
  checkIn: 'all' | 'present' = 'all',
) =>
  useQuery<EventAttendeesResponse>({
    queryKey: [EVENTS_QUERY_KEY, 'attendees', eventId, search, checkIn],
    queryFn: async () =>
      (
        await api.get<EventAttendeesResponse>(`/events/${eventId}/attendees`, {
          params: { search: search || undefined, checkIn, limit: 200 },
        })
      ).data,
    enabled: !!eventId && enabled && isLoggedIn(),
    placeholderData: keepPreviousData,
  })

export const useMyEvents = () =>
  useQuery<MyEventsResponse>({
    queryKey: [EVENTS_QUERY_KEY, 'mine'],
    queryFn: async () => (await api.get<MyEventsResponse>('/me/events')).data,
    enabled: isLoggedIn(),
  })

export const useOrganizerEvents = (enabled: boolean) =>
  useQuery<OrganizerEvent[]>({
    queryKey: [EVENTS_QUERY_KEY, 'organizer'],
    queryFn: async () => (await api.get<{ data: OrganizerEvent[] }>('/organizer/events')).data.data,
    enabled: enabled && isLoggedIn(),
  })

/** "Mes statistiques": one aggregated view across every event the organizer runs. */
export const useOrganizerStats = (enabled: boolean) =>
  useQuery<OrganizerStatsResponse>({
    queryKey: [EVENTS_QUERY_KEY, 'organizer-stats'],
    queryFn: async () => (await api.get<OrganizerStatsResponse>('/organizer/stats')).data,
    enabled: enabled && isLoggedIn(),
  })

export const useOrganizerAttendees = (
  eventId: string | undefined,
  search: string,
  checkIn: CheckInFilter,
) =>
  useQuery<OrganizerAttendeesResponse>({
    queryKey: [EVENTS_QUERY_KEY, 'organizer-attendees', eventId, search, checkIn],
    queryFn: async () =>
      (
        await api.get<OrganizerAttendeesResponse>(`/organizer/events/${eventId}/attendees`, {
          params: { search: search || undefined, checkIn, limit: 200 },
        })
      ).data,
    enabled: !!eventId && isLoggedIn(),
    placeholderData: keepPreviousData,
  })

/** How often the entrance screen re-reads the list, so two people can tick at once. */
export const CHECKIN_REFRESH_MS = 15_000
export const checkInLiveKey = (eventId: string | undefined) =>
  [EVENTS_QUERY_KEY, 'checkin-live', eventId] as const

/** Every attendee of the event (all pages), refreshed periodically, for the entrance screen. */
export const useCheckInAttendees = (eventId: string | undefined) =>
  useQuery<OrganizerAttendee[]>({
    queryKey: checkInLiveKey(eventId),
    queryFn: async () => {
      const all: OrganizerAttendee[] = []
      for (let page = 1; ; page++) {
        const { data } = await api.get<OrganizerAttendeesResponse>(
          `/organizer/events/${eventId}/attendees`,
          { params: { page, limit: 200 } },
        )
        all.push(...data.data.filter((attendee) => !attendee.unregistered))
        // A short page is the last one (the count may or may not include unregistered rows).
        if (data.data.length < 200) return all
      }
    },
    enabled: !!eventId && isLoggedIn(),
    refetchInterval: CHECKIN_REFRESH_MS,
    // A tab that is not in front (second phone, second window) keeps up as well.
    refetchIntervalInBackground: true,
    // Give up fast on a real network error (no response at all): the entrance
    // screen falls back to the offline manifest rather than spend the
    // default backoff (up to ~7s) stuck on a spinner.
    retry: (failures, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status
      return !!status && failures < 2
    },
  })

export const useEventInvitations = (eventId: string | undefined) =>
  useQuery<{ data: EventInvitation[]; metadata: { count: number; accepted: number } }>({
    queryKey: [EVENTS_QUERY_KEY, 'invitations', eventId],
    queryFn: async () =>
      (
        await api.get<{ data: EventInvitation[]; metadata: { count: number; accepted: number } }>(
          `/organizer/events/${eventId}/invitations`,
        )
      ).data,
    enabled: !!eventId && isLoggedIn(),
  })

export const useEventEmailCampaigns = (eventId: string | undefined) =>
  useQuery<{ data: EventEmailCampaign[] }>({
    queryKey: [EVENTS_QUERY_KEY, 'email-campaigns', eventId],
    queryFn: async () =>
      (await api.get<{ data: EventEmailCampaign[] }>(`/organizer/events/${eventId}/emails`)).data,
    enabled: !!eventId && isLoggedIn(),
  })

/** Simulated payment screen: the event, the amount and how long the seat is held. */
export const useSimulatedPayment = (paymentId: string) =>
  useQuery<SimulatedPaymentResponse>({
    queryKey: [EVENTS_QUERY_KEY, 'simulated-payment', paymentId],
    queryFn: async () =>
      (await api.get<SimulatedPaymentResponse>(`/payments/simulated/${paymentId}`)).data,
    enabled: !!paymentId,
    retry: false,
  })

/** Public upcoming events for the "Découvrir" tab, loaded page by page. */
export const useDiscoverEvents = (filters: DiscoveryFilters, enabled = true) =>
  useInfiniteQuery<DiscoverEventsResponse>({
    queryKey: [EVENTS_QUERY_KEY, 'discover', filters],
    queryFn: async ({ pageParam }) =>
      (
        await api.get<DiscoverEventsResponse>('/events/discover', {
          params: buildDiscoveryParams(filters, pageParam as number),
        })
      ).data,
    initialPageParam: 1,
    getNextPageParam: ({ metadata }) =>
      metadata.page * metadata.limit < metadata.count ? metadata.page + 1 : undefined,
    enabled: enabled && isLoggedIn(),
    placeholderData: keepPreviousData,
  })
